import { STOCK_VIDEO_ENGAGEMENT_COLLECTION } from './collections';

type AccountTypeGroup = 'paid' | 'free';

/** Default page slug used for the global stock video browse context. */
export const DEFAULT_VIDEO_ENGAGEMENT_SLUG = '/stock-video';

/**
 * Escape a value so it is safe to embed inside a double-quoted Typesense
 * string literal. Backslashes must be doubled first, then double-quotes
 * escaped, so that an input ending in `\` doesn't combine with the closing
 * quote to break out of the literal.
 *
 * Today the only caller is {@link getVideoEngagementJoinFilter} and slugs
 * come from internal category data, but this is cheap defence in depth in
 * case a route-derived slug ever reaches the filter.
 */
export const escapeTypesenseStringLiteral = (value: string): string =>
  value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');

/**
 * Build a Typesense left join filter clause for the video engagement collection.
 *
 * Uses the documented left join pattern `id:* || $collection(...)` so videos
 * without an engagement row for this (slug, account_type_group) are still
 * returned — they fall to the tail of the sort chain instead of being dropped.
 *
 * The join itself is wired via the schema's reference field
 * (`videoEngagementV1.video_id` → `videos.v1.id`); the clauses inside
 * `$videoEngagementV1(...)` only constrain which engagement row to join, never
 * the join key. Joining on a literal `video_id:=id` would compare against the
 * string "id" and silently produce zero matches.
 */
export const getVideoEngagementJoinFilter = (
  accountTypeGroup: AccountTypeGroup,
  slug: string = DEFAULT_VIDEO_ENGAGEMENT_SLUG
): string =>
  `id:* || $${STOCK_VIDEO_ENGAGEMENT_COLLECTION}(account_type_group:=${accountTypeGroup} && slug:="${escapeTypesenseStringLiteral(slug)}")`;

/**
 * Primary engagement sort: rank by joined engagement sort_score descending.
 * Append {@link VIDEO_RECENCY_TIE_BREAKER} (and `_vector_distance:asc` where
 * applicable) so unranked / tied videos still get a deterministic order.
 */
export const VIDEO_ENGAGEMENT_SORT = `$${STOCK_VIDEO_ENGAGEMENT_COLLECTION}(sort_score:desc)`;

/**
 * Final tie-breaker for every stock-video sort chain: prefer newer assets when
 * earlier sort fields are equal (very common because many videos share
 * `sort_score:0`). For vector searches, place `_vector_distance:asc` BEFORE
 * this so embedding proximity gets the next say after engagement.
 */
export const VIDEO_RECENCY_TIE_BREAKER = 'set_live:desc';

/**
 * `include_fields` value that returns every base video field plus the joined
 * engagement metrics nested under `videoEngagementV1`.
 * The metrics power the admin relevance debug overlay on tiles;
 * `account_type_group` labels which group's row ranked the asset.
 */
export const STOCK_VIDEO_INCLUDE_FIELDS = `*,$${STOCK_VIDEO_ENGAGEMENT_COLLECTION}(sort_score,total_downloads,total_plays,decay_downloads,decay_plays,slug_mean,account_type_group,strategy:nest)`;

/**
 * Keywords to exclude from the home page asset listing.
 * These are chosen to remove assets with poor thumbnails (such as blurs)
 * and assets that feel too "stocky".
 */
const EXCLUDED_KEYWORDS = [
  'abstract',
  'flag',
  'blur',
  'blurred',
  'motion_blur',
  'film_overlay',
  'overlay',
  'noise',
  'business',
  'car',
  'cars',
  'texture',
];

/**
 * Combine an optional base filter with the engagement left join filter.
 * The left join pattern (`id:* || $collection(...)`) ensures videos without
 * engagement data are still returned.
 */
export const withEngagementFilter = (
  accountTypeGroup: AccountTypeGroup,
  baseFilter?: string,
  slug: string = DEFAULT_VIDEO_ENGAGEMENT_SLUG
): string => {
  const joinFilter = getVideoEngagementJoinFilter(accountTypeGroup, slug);

  let engagementFilter = joinFilter;

  if (baseFilter) {
    engagementFilter = `(${baseFilter})`;

    /**
     * EXCLUDED_KEYWORDS is hardcoded for now so this can never be false,
     * but the plan is to move it to an admin area eventually.
     *
     * This should only apply on the SVL home page.
     */
    if (slug === '/stock-video' && EXCLUDED_KEYWORDS.length > 0) {
      engagementFilter += `&&keywords:![${EXCLUDED_KEYWORDS.join(',')}]`;
    }

    engagementFilter += `&&(${joinFilter})`;
  }

  return engagementFilter;
};

/**
 * Typesense errors when `sort_by` contains more than 3 sort fields. Use this
 * cap when composing chained sort expressions for stock-video searches.
 */
export const TYPESENSE_MAX_SORT_FIELDS = 3;

/**
 * Compose a Typesense `sort_by` string from ordered candidate clauses,
 * skipping empty entries, deduplicating, and capping at
 * {@link TYPESENSE_MAX_SORT_FIELDS}. Each part may itself be a
 * comma-separated chain — clauses are flattened so the cap applies to the
 * final field count Typesense actually sees.
 *
 * Earlier parts win on the cap, so put primary ranking first and recency /
 * tie-breakers last.
 */
/**
 * Split a Typesense sort chain on top-level commas only — commas inside
 * parentheses belong to function arguments (e.g. `_geo_distance(41.12,-71.34)`
 * or `$collection(filter1 && filter2)`) and must not be treated as field
 * separators.
 */
const splitTopLevelCommas = (input: string): string[] => {
  const parts: string[] = [];
  let depth = 0;
  let start = 0;
  for (let i = 0; i < input.length; i++) {
    const ch = input[i];
    if (ch === '(') {
      depth++;
    } else if (ch === ')') {
      depth = Math.max(0, depth - 1);
    } else if (ch === ',' && depth === 0) {
      parts.push(input.slice(start, i));
      start = i + 1;
    }
  }
  parts.push(input.slice(start));
  return parts;
};

export const composeStockVideoSort = (
  parts: ReadonlyArray<string | false | null | undefined>
): string => {
  const seen = new Set<string>();
  const fields: string[] = [];

  for (const part of parts) {
    if (!part) {
      continue;
    }
    for (const raw of splitTopLevelCommas(part)) {
      const field = raw.trim();
      if (!field || seen.has(field)) {
        continue;
      }
      seen.add(field);
      fields.push(field);
      if (fields.length >= TYPESENSE_MAX_SORT_FIELDS) {
        return fields.join(',');
      }
    }
  }

  return fields.join(',');
};
